/**
 * @jsx jsx
 */
import React from 'react';

import { keyframes, cssMap as nonStandardCssMap } from '@compiled/react';

import { default as Button, IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import MicrophoneIcon from '@atlaskit/icon/core/microphone';
import { type SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { isEmptyAdf } from '../model/adf';
import { type ContentType, contentTypeConfiguration } from '../model/ContentTypes';
import { type InputMethod, inputMethods } from '../model/InputMethods';
import { storePrompt, toggleContentType, updateInputMethod } from '../store/SmartCreateStore';

import { useSmartPreset } from './SmartCreateEditor';

const waveAnimation = keyframes({
	'0%, 100%': { transform: 'scaleY(0.3)' },
	'50%': { transform: 'scaleY(1)' },
});

const nonStandardStyles = nonStandardCssMap({
	gradientGlow: {
		position: 'absolute',
		width: '100%',
		height: '100%',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		background: 'linear-gradient(90deg, #1F69DA 0%, #C16CD4 29.33%, #F5A117 64.66%, #79AE44 100%)',
		filter: 'blur(25px)',
		opacity: token('opacity.disabled'),
		pointerEvents: 'none',
	},
});

const styles = cssMap({
	gradientGlowWrapper: {
		position: 'relative',
		display: 'inline-block',
	},
	container: {
		position: 'relative',
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.200'),
		minWidth: '575px',
		maxWidth: '85%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('border.radius.100'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	editor: {
		width: '100%',
		marginBottom: token('space.200'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	buttonSpace: {
		marginLeft: token('space.100'),
	},
	speechIndicator: {
		backgroundColor: token('color.background.information.bold'),
		borderRadius: token('border.radius.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
	},
	waveform: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.025'),
		height: '16px',
		minHeight: '16px',
	},
	waveBar: {
		width: '2px',
		height: '8px',
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('border.radius.050'),
		transformOrigin: 'center',
		animationName: waveAnimation,
		animationDuration: '1.2s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',
	},
});

const Waveform = () => {
	const bars = Array.from({ length: 5 }, (_, i) => i);
	const barHeights = [6, 10, 8, 12, 7];

	return (
		<Box xcss={styles.waveform}>
			{bars.map((_, index) => (
				<Box
					key={index}
					xcss={styles.waveBar}
					style={{
						height: `${barHeights[index]}px`,
						animationDelay: `${index * 0.15}s`,
					}}
				/>
			))}
		</Box>
	);
};

type InputBoxProps = {
	prompt: string;
	contentTypes: ContentType[];
	inputMethod: InputMethod | null;
	onCreate: () => void;
	SmartCardProvider: typeof SmartCardProvider;
	ErrorBoundary: React.ComponentType<{ children: React.ReactNode }>;
};

const placeholderOptions = {
	placeholderText: 'Describe whatever you want to create',
};

export const InputBox = ({
	onCreate,
	prompt,
	contentTypes,
	inputMethod,
	SmartCardProvider,
	ErrorBoundary,
}: InputBoxProps) => {
	const hasPrompt = prompt && isEmptyAdf(JSON.parse(prompt)) === false;

	const preset = useSmartPreset(placeholderOptions);
	const isSpeechActive = inputMethod === 'speech';

	const onSuppress: React.KeyboardEventHandler = (event) => {
		event.stopPropagation();
	};

	return (
		<Box xcss={styles.gradientGlowWrapper}>
			<div css={nonStandardStyles.gradientGlow} />
			<Box xcss={styles.container} style={{ border: `1.5px solid ${token('color.text.brand')}` }}>
				{isSpeechActive && (
					<Box xcss={styles.speechIndicator}>
						<Flex gap="space.100" alignItems="center">
							<MicrophoneIcon label="Microphone" size="small" color={token('color.icon.inverse')} />
							<Waveform />
							<Text size="small" color="color.text.inverse">
								Listening for your voice...
							</Text>
						</Flex>
					</Box>
				)}
				{/* editor */}
				{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
				<Box
					xcss={styles.editor}
					as="div"
					onKeyDown={onSuppress}
					onKeyUp={onSuppress}
					onKeyPress={onSuppress}
				>
					<SmartCardProvider product="CONFLUENCE">
						<EditorContext>
							<ErrorBoundary>
								<ChromelessEditor onChange={storePrompt} shouldFocus quickInsert preset={preset} />
							</ErrorBoundary>
						</EditorContext>
					</SmartCardProvider>
				</Box>
				<Flex direction="row" gap="space.200" justifyContent="space-between" alignItems="center">
					{/* left items */}
					<Flex columnGap="space.050">
						{Object.entries(contentTypeConfiguration).map(([key, { icon: Icon, tooltip }]) => (
							<Tooltip content={tooltip} key={key}>
								<IconButton
									icon={(iconProps) => <Icon {...iconProps} label={tooltip} size="medium" />}
									label={tooltip}
									appearance="subtle"
									isSelected={contentTypes.includes(key as ContentType)}
									onClick={() => toggleContentType(key as ContentType)}
								/>
							</Tooltip>
						))}
					</Flex>
					{/* right items */}
					<Flex columnGap="space.050">
						{Object.entries(inputMethods).map(([key, { icon: Icon, tooltip }]) => (
							<Tooltip content={tooltip} key={key}>
								<IconButton
									icon={(iconProps) => <Icon {...iconProps} label={tooltip} size="medium" />}
									label={tooltip}
									appearance="subtle"
									onClick={() => updateInputMethod(key as InputMethod)}
									isSelected={inputMethod === key}
								/>
							</Tooltip>
						))}
						<Box xcss={styles.buttonSpace} />
						{/* CTA */}
						<Button appearance="primary" onClick={onCreate} isDisabled={!hasPrompt}>
							Create
						</Button>
					</Flex>
				</Flex>
			</Box>
		</Box>
	);
};
